import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteContent, bapuImage } from '../data/mock';
import { ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const { language } = useLanguage();
  const content = siteContent[language].hero;

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 md:pt-20"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-orange-600 via-orange-500 to-amber-400" />
      
      {/* Decorative Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px'
        }} />
      </div>

      {/* Floating Decorative Elements */}
      <div className="absolute top-20 left-10 w-16 md:w-20 h-16 md:h-20 bg-white/10 rounded-full blur-2xl animate-pulse" />
      <div className="absolute bottom-40 right-10 w-24 md:w-32 h-24 md:h-32 bg-amber-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/3 right-20 w-12 md:w-16 h-12 md:h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-20 text-center">
        {/* Greeting */}
        <p className="text-white/90 text-base md:text-lg lg:text-xl mb-3 md:mb-4 tracking-wide animate-fade-in">
          {content.greeting}
        </p>

        {/* Bapu Image */}
        <div className="relative mx-auto mb-6 md:mb-8 w-40 h-40 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-72 lg:h-72">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-300 to-orange-400 rounded-full blur-xl opacity-50 animate-pulse" />
          <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-white/30 shadow-2xl">
            <img
              src={bapuImage}
              alt="Param Pujya Guru Shri Umedpuri Maharaj"
              className="w-full h-full object-cover object-top"
            />
          </div>
          {/* Decorative Ring */}
          <div className="absolute -inset-2 border-2 border-white/20 rounded-full" />
          <div className="absolute -inset-4 border border-white/10 rounded-full hidden md:block" />
        </div>

        {/* Main Spiritual Line */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-3 md:mb-4 tracking-tight drop-shadow-lg">
          {content.mainLine}
        </h1>

        {/* Meaning */}
        <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-amber-100 mb-4 md:mb-6 font-medium">
          "{content.meaning}"
        </p>

        {/* Subtitle */}
        <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed px-2">
          {content.subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4">
          <button
            onClick={() => scrollToSection('#about')}
            className="px-6 md:px-8 py-3 bg-white text-orange-600 font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-sm md:text-base"
          >
            {content.buttons.about}
          </button>
          <button
            onClick={() => scrollToSection('#teachings')}
            className="px-6 md:px-8 py-3 bg-white/20 text-white font-semibold rounded-full border-2 border-white/40 hover:bg-white/30 transform hover:-translate-y-1 transition-all duration-300 text-sm md:text-base"
          >
            {content.buttons.teachings}
          </button>
          <button
            onClick={() => scrollToSection('#contact')}
            className="px-6 md:px-8 py-3 bg-amber-400 text-orange-900 font-semibold rounded-full shadow-lg hover:shadow-xl hover:bg-amber-300 transform hover:-translate-y-1 transition-all duration-300 text-sm md:text-base"
          >
            {content.buttons.contact}
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 md:w-8 md:h-8 text-white/60" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
